/* Client-side fuzzy layer over Shopify storefront search.
 *
 * Adds two behaviours Shopify's search lacks:
 *   1. Space-agnostic matching — "davinci" finds "Da Vinci" and vice versa.
 *   2. Typo tolerance — Levenshtein distance with a length-scaled margin.
 *
 * It never replaces Shopify search; it derives a *corrected query* from a
 * lightweight product-title index (fetched once via the Section Rendering
 * API, cached in localStorage) and the caller re-runs the normal search
 * with that corrected query.
 *
 * Public API: window.WFFuzzySearch.correct(query) -> Promise<string|null>
 */
(function () {
  'use strict';

  var CACHE_KEY = 'wf:search-index:v1';
  var CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours
  var MAX_PAGES = 40; // safety cap: 40 pages x 250 products
  var FETCH_BATCH = 6; // index pages fetched concurrently per round
  var WINDOW_MAX = 4; // compare against up to 4 consecutive title words

  var cfg = window.WFSearchConfig || {};
  var rootUrl = cfg.root || '/';
  var index = null; // [{ tokens: [...] }]
  var pendingLoad = null;

  /* Combining diacritical marks, U+0300 through U+036F (built via char codes
     so this file stays pure ASCII). */
  var DIACRITICS = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

  /* Lowercase, strip diacritics, collapse everything non-alphanumeric to a
     single space. "Da-Vinci  Brush" -> "da vinci brush". */
  function normalize(s) {
    return String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(DIACRITICS, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /* Damerau-Levenshtein distance (optimal string alignment: an adjacent
     transposition counts as ONE edit, since swapped letters are the most
     common typo). Early exit once the distance is known to exceed `max`
     (returns max + 1 in that case). */
  function levenshtein(a, b, max) {
    if (a === b) return 0;
    var la = a.length;
    var lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var prev2 = new Array(lb + 1); // row i-2, for the transposition case
    var prev = new Array(lb + 1);
    var curr = new Array(lb + 1);
    var i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      curr[0] = i;
      var rowMin = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
        if (
          i > 1 && j > 1 &&
          a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
          a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
        ) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        curr[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      var swap = prev2;
      prev2 = prev;
      prev = curr;
      curr = swap;
    }
    return prev[lb] > max ? max + 1 : prev[lb];
  }

  /* Typo margin scales with query length: short queries only get exact
     (space-variant/substring) matches, longer ones allow 1-2 edits. */
  function maxDistanceFor(len) {
    if (len < 4) return 0;
    if (len < 8) return 1;
    return 2;
  }

  /* The Section Rendering API wraps the section's JSON in a wrapper div;
     slice out the outermost object. */
  function parseSectionJson(text) {
    var start = text.indexOf('{');
    var end = text.lastIndexOf('}');
    if (start === -1 || end <= start) throw new Error('search-index: no JSON payload');
    return JSON.parse(text.slice(start, end + 1));
  }

  function fetchPage(page) {
    return fetch(rootUrl + '?section_id=search-index&page=' + page).then(function (r) {
      if (!r.ok) throw new Error('search-index: HTTP ' + r.status);
      return r.text();
    }).then(parseSectionJson);
  }

  function loadItems() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && cached.ts && Date.now() - cached.ts < CACHE_TTL && cached.items) {
          return Promise.resolve(cached.items);
        }
      }
    } catch (e) { /* blocked storage (private mode) — fall through to fetch */ }

    return fetchPage(1).then(function (first) {
      var pages = Math.min(first.pages || 1, MAX_PAGES);
      var queue = [];
      for (var p = 2; p <= pages; p++) queue.push(p);

      // Fetch the remaining pages in batches of FETCH_BATCH so a large
      // catalog doesn't fire dozens of simultaneous requests.
      function drain(collected) {
        if (!queue.length) return Promise.resolve(collected);
        var batch = queue.splice(0, FETCH_BATCH).map(fetchPage);
        return Promise.all(batch).then(function (results) {
          for (var k = 0; k < results.length; k++) collected = collected.concat(results[k].items);
          return drain(collected);
        });
      }

      return drain(first.items).then(function (items) {
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: items }));
        } catch (e) { /* quota/private mode: in-memory only */ }
        return items;
      });
    });
  }

  function ensureIndex() {
    if (index) return Promise.resolve(index);
    if (!pendingLoad) {
      pendingLoad = loadItems().then(function (items) {
        index = items.map(function (it) {
          var tokens = normalize(it.t + (it.v ? ' ' + it.v : '')).split(' ');
          return { tokens: tokens };
        });
        return index;
      }).catch(function (err) {
        pendingLoad = null; // allow a retry on the next call
        throw err;
      });
    }
    return pendingLoad;
  }

  /* Find the best corrected query for `query`, or null when nothing beats
     the typo margin. Every window of 1..WINDOW_MAX consecutive title words
     is concatenated (spaces dropped) and compared against the space-stripped
     query, so word-boundary differences cost nothing. */
  function findCorrection(query) {
    var nq = normalize(query);
    var qs = nq.replace(/ /g, '');
    if (qs.length < 3) return Promise.resolve(null);
    var maxDist = maxDistanceFor(qs.length);

    return ensureIndex().then(function (idx) {
      // Two candidate tiers. Strong: the space-stripped query equals a window
      // (spacing variant) or sits within the typo margin — a match against the
      // COMPLETE query. Weak: the query is a prefix of / contained in a window
      // (the user may still be typing). Weak only wins when nothing strong
      // exists, otherwise "wildgrass" would "correct" to "wildgras scenery"
      // instead of "wildgras".
      var strong = Object.create(null); // term -> { dist, count }
      var weak = Object.create(null);
      var queryIsKnown = false; // query already matches a window exactly

      for (var i = 0; i < idx.length; i++) {
        var tokens = idx[i].tokens;
        var seenInProduct = Object.create(null);
        for (var s = 0; s < tokens.length; s++) {
          var w = '';
          for (var e = s; e < tokens.length && e - s < WINDOW_MAX; e++) {
            w += tokens[e];
            var term = tokens.slice(s, e + 1).join(' ');
            if (term === nq) {
              // The query itself is a known term: a rewrite cannot improve on
              // what the server already searched for.
              if (w === qs) queryIsKnown = true;
              continue;
            }

            var bucket = null;
            var dist;
            if (w === qs) {
              bucket = strong;
              dist = 0; // pure spacing variant
            } else if (w.length >= qs.length && w.indexOf(qs) === 0) {
              bucket = weak;
              dist = 0.25; // query is a prefix of the window
            } else if (w.indexOf(qs) !== -1) {
              bucket = weak;
              dist = 0.5; // query contained inside the window
            } else if (maxDist > 0 && Math.abs(w.length - qs.length) <= maxDist) {
              var d = levenshtein(qs, w, maxDist);
              if (d > maxDist) continue;
              bucket = strong;
              dist = d;
            } else {
              continue;
            }

            var sc = bucket[term];
            if (!sc) sc = bucket[term] = { dist: dist, count: 0 };
            if (dist < sc.dist) sc.dist = dist;
            if (!seenInProduct[term]) {
              seenInProduct[term] = true;
              sc.count += 1;
            }
          }
        }
      }

      if (queryIsKnown) return null;

      function pickBest(scores) {
        var best = null;
        var bestTerm = null;
        for (var t in scores) {
          var sc = scores[t];
          if (
            !best ||
            sc.dist < best.dist ||
            (sc.dist === best.dist && sc.count > best.count) ||
            (sc.dist === best.dist && sc.count === best.count && t.length < bestTerm.length)
          ) {
            best = sc;
            bestTerm = t;
          }
        }
        return bestTerm;
      }

      return pickBest(strong) || pickBest(weak);
    });
  }

  window.WFFuzzySearch = {
    correct: function (query) {
      return findCorrection(query).catch(function () { return null; });
    }
  };

  /* Prewarm: building the index takes a few seconds on a cold cache, which
     would make the very first correction miss its moment. Start loading as
     soon as the user shows search intent (focuses any search field) so the
     index is ready by the time they finish typing. */
  document.addEventListener('focusin', function (e) {
    var el = e.target;
    if (el && el.matches && el.matches('input[type="search"], .search-input')) {
      ensureIndex().catch(function () {});
    }
  });
})();
